import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LithuanianTimePicker } from '@/components/ui/lithuanian-time-picker';
import { toast } from 'sonner';
import { 
  X, 
  User, 
  Heart, 
  Clock, 
  MapPin,
  Phone,
  Mail,
  MessageCircle,
  Send,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '@/utils/apiUrl';

const BACKEND_URL = API_BASE_URL;

// Relationship status options
const RELATIONSHIP_STATUSES = [
  { value: 'single', label: 'Neturiu partnerio' },
  { value: 'dating', label: 'Draugaujame' },
  { value: 'living_together', label: 'Gyvename kartu' },
  { value: 'married', label: 'Susituokę' },
  { value: 'separated', label: 'Išsiskyrę' },
  { value: 'divorced', label: 'Išsituokę' },
  { value: 'other', label: 'Kita' }
];

// Contact method options
const CONTACT_METHODS = [
  { value: 'phone', label: 'Telefonu', icon: Phone },
  { value: 'email', label: 'El. paštu', icon: Mail },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'telegram', label: 'Telegram', icon: Send }
];

// Lithuanian Keyboard Component
const LithuanianKeyboard = ({ onInsert }) => {
  const letters = ['ą', 'č', 'ę', 'ė', 'į', 'š', 'ų', 'ū', 'ž', 'Ą', 'Č', 'Ę', 'Ė', 'Į', 'Š', 'Ų', 'Ū', 'Ž'];
  
  return (
    <div className="flex flex-wrap gap-1 justify-center p-2 bg-secondary/30 rounded-lg border border-border/50">
      {letters.map((letter) => (
        <button
          key={letter}
          type="button"
          onClick={() => onInsert(letter)}
          className="w-7 h-7 rounded bg-amber-500/20 hover:bg-amber-500/40 text-amber-400 font-medium text-xs transition-all duration-200 border border-amber-500/30"
        >
          {letter}
        </button>
      ))}
    </div>
  );
};

/**
 * VIP Profile Completion Modal
 * For Yearly VIP and VIP+ users to add birth time/place for PDF generation
 */
export function VIPProfileModal({ isOpen, onClose, onSuccess, user, token, isPremium = false }) {
  const [loading, setLoading] = useState(false);
  const [activeField, setActiveField] = useState('birthPlace');
  const [formData, setFormData] = useState({
    birthTime: '',
    birthPlace: '',
    hasPartner: false,
    partner: {
      firstName: '',
      lastName: '',
      birthDay: '',
      birthMonth: '',
      birthYear: '',
      birthTime: '',
      birthPlace: ''
    },
    relationshipStatus: 'single',
    analysisContext: '',
    contactMethod: 'email',
    contactValue: user?.email || ''
  });

  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({ ...prev, contactValue: user.email }));
    }
  }, [user]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.birthTime || !formData.birthPlace) {
      toast.error('Prašome užpildyti gimimo laiką ir vietą');
      return;
    }

    if (isPremium && !formData.contactValue) {
      toast.error('Prašome nurodyti susisiekimo būdą');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/users/vip-profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Profilis atnaujintas sėkmingai!');
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Klaida išsaugant duomenis');
    } finally {
      setLoading(false);
    }
  };

  const insertLetter = (letter) => {
    if (activeField === 'birthPlace') {
      setFormData(prev => ({ ...prev, birthPlace: prev.birthPlace + letter }));
    } else if (activeField === 'partnerFirstName') {
      setFormData(prev => ({ 
        ...prev, 
        partner: { ...prev.partner, firstName: prev.partner.firstName + letter }
      }));
    } else if (activeField === 'partnerLastName') {
      setFormData(prev => ({ 
        ...prev, 
        partner: { ...prev.partner, lastName: prev.partner.lastName + letter }
      }));
    } else if (activeField === 'partnerBirthPlace') {
      setFormData(prev => ({ 
        ...prev, 
        partner: { ...prev.partner, birthPlace: prev.partner.birthPlace + letter }
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="card-mystical max-w-lg w-full p-6 relative my-8 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-amber-500/40">
            <User className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="font-display text-xl text-amber-400">VIP Profilio užbaigimas</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Šie duomenys reikalingi jūsų asmeninei PDF Sielos Knygai ir suderinamumo analizei
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Birth Time & Place */}
          <div className="bg-slate-900/50 rounded-lg p-4 border border-amber-500/20">
            <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Jūsų gimimo duomenys
            </h3>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-foreground/70 text-xs mb-1 block">Gimimo laikas *</Label>
                <LithuanianTimePicker
                  value={formData.birthTime}
                  onChange={(time) => setFormData({ ...formData, birthTime: time })}
                  placeholder="Pasirinkti laiką"
                />
              </div>
              <div>
                <Label className="text-foreground/70 text-xs mb-1 block">Gimimo vieta *</Label>
                <Input
                  value={formData.birthPlace}
                  onChange={(e) => setFormData({ ...formData, birthPlace: e.target.value })}
                  onFocus={() => setActiveField('birthPlace')}
                  placeholder="Miestas, Šalis"
                  className="bg-input border-border"
                />
              </div>
            </div>
            
            <div className="mt-3">
              <LithuanianKeyboard onInsert={insertLetter} />
            </div>
          </div>

          {/* Partner Toggle */}
          <div className="bg-slate-900/50 rounded-lg p-4 border border-pink-500/20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-pink-400 flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Partnerio duomenys
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!formData.hasPartner}
                  onChange={(e) => setFormData({ ...formData, hasPartner: !e.target.checked })}
                  className="sr-only"
                />
                <span className={`text-xs ${!formData.hasPartner ? 'text-pink-400' : 'text-muted-foreground'}`}>
                  Šiuo metu partnerio neturiu
                </span>
                <div className={`w-10 h-5 rounded-full transition-colors ${!formData.hasPartner ? 'bg-pink-500' : 'bg-slate-700'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white transform transition-transform mt-0.5 ${!formData.hasPartner ? 'translate-x-5' : 'translate-x-1'}`} />
                </div>
              </label>
            </div>

            {formData.hasPartner && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-foreground/70 text-xs mb-1 block">Vardas</Label>
                    <Input
                      value={formData.partner.firstName}
                      onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, firstName: e.target.value } })}
                      onFocus={() => setActiveField('partnerFirstName')}
                      className="bg-input border-border"
                    />
                  </div>
                  <div>
                    <Label className="text-foreground/70 text-xs mb-1 block">Pavardė</Label>
                    <Input
                      value={formData.partner.lastName}
                      onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, lastName: e.target.value } })}
                      onFocus={() => setActiveField('partnerLastName')}
                      className="bg-input border-border"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-foreground/70 text-xs mb-1 block">Gimimo data</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.partner.birthDay}
                      onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, birthDay: e.target.value } })}
                      placeholder="DD"
                      className="bg-input border-border text-center"
                    />
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      value={formData.partner.birthMonth}
                      onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, birthMonth: e.target.value } })}
                      placeholder="MM"
                      className="bg-input border-border text-center"
                    />
                    <Input
                      type="number"
                      value={formData.partner.birthYear}
                      onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, birthYear: e.target.value } })}
                      placeholder="YYYY"
                      className="bg-input border-border text-center"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-foreground/70 text-xs mb-1 block">Gimimo laikas</Label>
                    <LithuanianTimePicker
                      value={formData.partner.birthTime}
                      onChange={(time) => setFormData({ ...formData, partner: { ...formData.partner, birthTime: time } })}
                      placeholder="Pasirinkti laiką"
                    />
                  </div>
                  <div>
                    <Label className="text-foreground/70 text-xs mb-1 block">Gimimo vieta</Label>
                    <Input
                      value={formData.partner.birthPlace}
                      onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, birthPlace: e.target.value } })}
                      onFocus={() => setActiveField('partnerBirthPlace')}
                      placeholder="Miestas"
                      className="bg-input border-border"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-foreground/70 text-xs mb-1 block">Santykių statusas</Label>
                  <select
                    value={formData.relationshipStatus}
                    onChange={(e) => setFormData({ ...formData, relationshipStatus: e.target.value })}
                    className="w-full p-2 rounded-lg bg-input border border-border text-foreground text-sm"
                  >
                    {RELATIONSHIP_STATUSES.filter(s => s.value !== 'single').map((status) => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label className="text-foreground/70 text-xs mb-1 block">
                    Ką norėtumėte sužinoti apie santykius?
                  </Label>
                  <Textarea
                    value={formData.analysisContext}
                    onChange={(e) => setFormData({ ...formData, analysisContext: e.target.value })}
                    placeholder="Pvz.: Noriu kurti šeimą, Krizė santykiuose, Noriu suprasti partnerį geriau..."
                    className="bg-input border-border min-h-[80px]"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Contact Method - Only for Premium */}
          {isPremium && (
            <div className="bg-slate-900/50 rounded-lg p-4 border border-purple-500/20">
              <h3 className="text-sm font-semibold text-purple-400 mb-3 flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                Susisiekimo būdas (konsultacijoms) *
              </h3>
              
              <div className="grid grid-cols-2 gap-2 mb-3">
                {CONTACT_METHODS.map((method) => {
                  const Icon = method.icon;
                  return (
                    <button
                      key={method.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, contactMethod: method.value })}
                      className={`p-2 rounded-lg border transition-colors flex items-center gap-2 ${
                        formData.contactMethod === method.value
                          ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                          : 'border-border/50 text-muted-foreground hover:border-purple-500/50'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="text-sm">{method.label}</span>
                    </button>
                  );
                })}
              </div>
              
              <Input
                value={formData.contactValue}
                onChange={(e) => setFormData({ ...formData, contactValue: e.target.value })}
                placeholder={
                  formData.contactMethod === 'phone' ? '+370...' :
                  formData.contactMethod === 'email' ? 'jusu@email.lt' :
                  formData.contactMethod === 'whatsapp' ? '+370...' :
                  '@username'
                }
                className="bg-input border-border"
              />
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold py-5"
          >
            {loading ? 'Išsaugoma...' : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Išsaugoti VIP profilį
              </>
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}

/**
 * VIP+ Consultation Request Modal
 * Appears after €65 payment - collects consultation data and sends to info@numenaxis.com
 */
export function ConsultationRequestModal({ isOpen, onClose, onSuccess, user, token }) {
  const [loading, setLoading] = useState(false);
  const [activeField, setActiveField] = useState('');
  const [formData, setFormData] = useState({
    partner: {
      firstName: '',
      lastName: '',
      birthDay: '',
      birthMonth: '',
      birthYear: '',
      birthTime: '',
      birthPlace: ''
    },
    relationshipStatus: 'dating',
    consultationTopic: '',
    contactMethod: 'email',
    contactValue: user?.email || ''
  });

  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({ ...prev, contactValue: user.email }));
    }
  }, [user]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consultationTopic) {
      toast.error('Prašome aprašyti užklausos esmę');
      return;
    }

    if (!formData.contactValue) {
      toast.error('Prašome nurodyti susisiekimo būdą');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/users/consultation-request`,
        {
          ...formData,
          userName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Užklausa išsiųsta! Susisieksime su jumis netrukus.');
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Klaida siunčiant užklausą');
    } finally {
      setLoading(false);
    }
  };

  const insertLetter = (letter) => {
    if (activeField === 'partnerFirstName') {
      setFormData(prev => ({ 
        ...prev, 
        partner: { ...prev.partner, firstName: prev.partner.firstName + letter }
      }));
    } else if (activeField === 'partnerLastName') {
      setFormData(prev => ({ 
        ...prev, 
        partner: { ...prev.partner, lastName: prev.partner.lastName + letter }
      }));
    } else if (activeField === 'partnerBirthPlace') {
      setFormData(prev => ({ 
        ...prev, 
        partner: { ...prev.partner, birthPlace: prev.partner.birthPlace + letter }
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <Card className="card-mystical max-w-lg w-full p-6 relative my-8 max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10"
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-purple-500/40">
            <MessageCircle className="w-8 h-8 text-purple-400" />
          </div>
          <h2 className="font-display text-xl text-purple-400">Konsultacijos užklausos forma</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Užpildykite formą, kad galėčiau pasiruošti jūsų asmeninei konsultacijai
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Partner Data */}
          <div className="bg-slate-900/50 rounded-lg p-4 border border-pink-500/20">
            <h3 className="text-sm font-semibold text-pink-400 mb-3 flex items-center gap-2">
              <Heart className="w-4 h-4" />
              Partnerio duomenys (jei aktualu)
            </h3>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-foreground/70 text-xs mb-1 block">Vardas</Label>
                  <Input
                    value={formData.partner.firstName}
                    onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, firstName: e.target.value } })}
                    onFocus={() => setActiveField('partnerFirstName')}
                    className="bg-input border-border"
                  />
                </div>
                <div>
                  <Label className="text-foreground/70 text-xs mb-1 block">Pavardė</Label>
                  <Input
                    value={formData.partner.lastName}
                    onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, lastName: e.target.value } })}
                    onFocus={() => setActiveField('partnerLastName')}
                    className="bg-input border-border"
                  />
                </div>
              </div>

              <div>
                <Label className="text-foreground/70 text-xs mb-1 block">Gimimo data</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.partner.birthDay}
                    onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, birthDay: e.target.value } })}
                    placeholder="DD"
                    className="bg-input border-border text-center"
                  />
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    value={formData.partner.birthMonth}
                    onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, birthMonth: e.target.value } })}
                    placeholder="MM"
                    className="bg-input border-border text-center"
                  />
                  <Input
                    type="number"
                    value={formData.partner.birthYear}
                    onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, birthYear: e.target.value } })}
                    placeholder="YYYY"
                    className="bg-input border-border text-center"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-foreground/70 text-xs mb-1 block">Gimimo laikas</Label>
                  <LithuanianTimePicker
                    value={formData.partner.birthTime}
                    onChange={(time) => setFormData({ ...formData, partner: { ...formData.partner, birthTime: time } })}
                    placeholder="Pasirinkti laiką"
                  />
                </div>
                <div>
                  <Label className="text-foreground/70 text-xs mb-1 block">Gimimo vieta</Label>
                  <Input
                    value={formData.partner.birthPlace}
                    onChange={(e) => setFormData({ ...formData, partner: { ...formData.partner, birthPlace: e.target.value } })}
                    onFocus={() => setActiveField('partnerBirthPlace')}
                    placeholder="Miestas"
                    className="bg-input border-border"
                  />
                </div>
              </div>

              <div className="mt-2">
                <LithuanianKeyboard onInsert={insertLetter} />
              </div>
            </div>
          </div>

          {/* Relationship Status */}
          <div className="bg-slate-900/50 rounded-lg p-4 border border-border/30">
            <Label className="text-foreground/70 text-xs mb-2 block">Santykių statusas</Label>
            <select
              value={formData.relationshipStatus}
              onChange={(e) => setFormData({ ...formData, relationshipStatus: e.target.value })}
              className="w-full p-2 rounded-lg bg-input border border-border text-foreground text-sm"
            >
              {RELATIONSHIP_STATUSES.map((status) => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>

          {/* Consultation Topic */}
          <div className="bg-slate-900/50 rounded-lg p-4 border border-amber-500/20">
            <h3 className="text-sm font-semibold text-amber-400 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Užklausos esmė *
            </h3>
            <Textarea
              value={formData.consultationTopic}
              onChange={(e) => setFormData({ ...formData, consultationTopic: e.target.value })}
              placeholder="Aprašykite, kas jums aktualu. Pvz.: Noriu skirtis, Noriu kurti šeimą, Krizė santykiuose, Karjeros pasirinkimas..."
              className="bg-input border-border min-h-[100px]"
            />
          </div>

          {/* Contact Method */}
          <div className="bg-slate-900/50 rounded-lg p-4 border border-purple-500/20">
            <h3 className="text-sm font-semibold text-purple-400 mb-3 flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Susisiekimo būdas *
            </h3>
            
            <div className="grid grid-cols-2 gap-2 mb-3">
              {CONTACT_METHODS.map((method) => {
                const Icon = method.icon;
                return (
                  <button
                    key={method.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, contactMethod: method.value })}
                    className={`p-2 rounded-lg border transition-colors flex items-center gap-2 ${
                      formData.contactMethod === method.value
                        ? 'border-purple-500 bg-purple-500/10 text-purple-400'
                        : 'border-border/50 text-muted-foreground hover:border-purple-500/50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm">{method.label}</span>
                  </button>
                );
              })}
            </div>
            
            <Input
              value={formData.contactValue}
              onChange={(e) => setFormData({ ...formData, contactValue: e.target.value })}
              placeholder={
                formData.contactMethod === 'phone' ? '+370...' :
                formData.contactMethod === 'email' ? 'jusu@email.lt' :
                formData.contactMethod === 'whatsapp' ? '+370...' :
                '@username'
              }
              className="bg-input border-border"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold py-5"
          >
            {loading ? 'Siunčiama...' : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Išsiųsti užklausą
              </>
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}

export default VIPProfileModal;
